import React, { FC, useState, useRef, useCallback, useEffect } from 'react';

import { UserObjectType } from 'constants/graphqlTypes';
import Avatar from 'components/UI/Avatar';
import UILink from 'components/UI/Link';
import { transformRoute } from 'helpers/routingHelper';
import { ROUTE_USER_PROFILE } from 'routes';
import useThoughtsModal from 'helpers/useThoughtsModal';
import withHapticFeedback from 'helpers/withHapticFeedback';
import ReactionMenu from 'components/Reactions/ReactionMenu/ReactionMenu';
import { ReactComponent as AddReaction } from 'assets/icons/AddReaction.svg';

import {
  StyledSparkCardHeader,
  StyledSparkCardHeaderLeft,
  StyledSparkCardHeaderAvatar,
  StyledSparkCardHeaderText,
  StyledSparkCardHeaderReactionButton,
  StyledExpandedHeader,
  StyledExpandedHeaderTop,
  StyledExpandedHeaderAvatar,
  StyledExpandedHeaderInfo,
  StyledExpandedHeaderName,
  StyledExpandedHeaderBio,
  StyledExpandedHeaderBottom,
  StyledExpandedHeaderThought,
  StyledExpandedHeaderTimestamp,
  StyledExpandedHeaderReactionButton,
  StyledSparkCardHeaderTimestamp,
} from './SparkCardHeader.styled';

interface SparkCardHeaderProps {
  user: UserObjectType;
  text?: string | null;
  timestamp?: string | null; // Fallback date text when no thought is present
  pickId?: string; // Used to open the thoughts modal on click
  fontColor?: string;
  isAnimating?: boolean; // Controlled by parent SparkCard via IntersectionObserver
  expanded?: boolean; // Use expanded layout with larger avatar and bio
}

// Typing speed in ms per character
const TYPING_SPEED_MS = 20;

/**
 * SparkCardHeader - Displays avatar, thought/fallback text, and reaction menu
 * Only rendered for other users' sparks (controlled by parent SparkCard)
 * Supports two modes:
 * - Compact (default): Small avatar with thought text inline
 * - Expanded: Larger avatar with name, bio, and thought stacked vertically
 */
const SparkCardHeader: FC<SparkCardHeaderProps> = ({
  user,
  text,
  timestamp,
  pickId,
  fontColor,
  isAnimating = false,
  expanded = false,
}) => {
  // Typewriter effect state
  const [displayedText, setDisplayedText] = useState('');
  const charIndexRef = useRef(0);
  const hasStartedTypingRef = useRef(false);

  // Typewriter effect - starts when isAnimating becomes true
  useEffect(() => {
    if (!isAnimating || !text || hasStartedTypingRef.current) {
      return undefined;
    }

    hasStartedTypingRef.current = true;

    const typeNextChar = () => {
      if (charIndexRef.current < text.length) {
        charIndexRef.current += 1;
        setDisplayedText(text.slice(0, charIndexRef.current));
      }
    };

    const intervalId = setInterval(typeNextChar, TYPING_SPEED_MS);

    return () => clearInterval(intervalId);
  }, [isAnimating, text]);

  // Open thoughts modal when thought/timestamp area is tapped
  const openThoughts = useThoughtsModal({
    id: pickId || '',
    openFromCardDetailPage: true,
  });

  const handleThoughtClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (pickId) {
      withHapticFeedback(openThoughts)();
    }
  };

  // Reaction menu state
  const [isReactionMenuOpen, setIsReactionMenuOpen] = useState(false);
  const [reactionMenuCoords, setReactionMenuCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const reactionButtonRef = useRef<HTMLButtonElement>(null);

  // Open reaction menu
  const openReactionMenu = useCallback(() => {
    if (reactionButtonRef.current) {
      const rect = reactionButtonRef.current.getBoundingClientRect();
      setReactionMenuCoords({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
    setIsReactionMenuOpen(true);
  }, []);

  // Close reaction menu
  const closeReactionMenu = useCallback(() => {
    setIsReactionMenuOpen(false);
  }, []);

  // Get user display name and bio
  const firstName = user.userInfo?.firstName || '';
  const lastName = user.userInfo?.lastName || '';
  const displayName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || user.username || '';
  const userBio = user.userInfo?.bio || '';

  // Expanded header layout (Featured/Following feeds)
  if (expanded) {
    return (
      <StyledExpandedHeader>
        <StyledExpandedHeaderTop>
          <StyledExpandedHeaderAvatar isVisible={isAnimating}>
            <UILink route={transformRoute(ROUTE_USER_PROFILE, { profileName: user.username })}>
              <Avatar user={user} dimension={40} clickable />
            </UILink>
          </StyledExpandedHeaderAvatar>

          <StyledExpandedHeaderInfo isVisible={isAnimating}>
            <StyledExpandedHeaderName fontColor={fontColor}>{displayName}</StyledExpandedHeaderName>
            {userBio && <StyledExpandedHeaderBio fontColor={fontColor}>{userBio}</StyledExpandedHeaderBio>}
          </StyledExpandedHeaderInfo>
        </StyledExpandedHeaderTop>

        <StyledExpandedHeaderBottom>
          {(() => {
            if (displayedText) {
              return (
                <StyledExpandedHeaderThought
                  fontColor={fontColor}
                  onClick={handleThoughtClick}
                  role="button"
                  tabIndex={0}
                >
                  {displayedText}
                </StyledExpandedHeaderThought>
              );
            }
            if (timestamp) {
              return (
                <StyledExpandedHeaderTimestamp
                  fontColor={fontColor}
                  onClick={handleThoughtClick}
                  role="button"
                  tabIndex={0}
                >
                  {timestamp}
                </StyledExpandedHeaderTimestamp>
              );
            }
            return null;
          })()}
          <StyledExpandedHeaderReactionButton
            ref={reactionButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openReactionMenu();
            }}
            aria-label="Add Reaction"
            isVisible={isAnimating}
          >
            <AddReaction style={{ color: fontColor }} />
          </StyledExpandedHeaderReactionButton>
        </StyledExpandedHeaderBottom>

        {isReactionMenuOpen && (
          <ReactionMenu
            isOpen={isReactionMenuOpen}
            anchorPoint={reactionMenuCoords}
            onClose={closeReactionMenu}
            onSend={() => {}}
            onEmoji={() => {}}
            onOpenReactionsModal={() => {}}
          />
        )}
      </StyledExpandedHeader>
    );
  }

  // Compact header layout (Profile page)
  return (
    <StyledSparkCardHeader>
      <StyledSparkCardHeaderLeft>
        <StyledSparkCardHeaderAvatar isVisible={isAnimating}>
          <UILink route={transformRoute(ROUTE_USER_PROFILE, { profileName: user.username })}>
            <Avatar user={user} dimension={20} clickable />
          </UILink>
        </StyledSparkCardHeaderAvatar>

        {(() => {
          const compactText = displayedText || text;
          if (compactText) {
            return (
              <StyledSparkCardHeaderText fontColor={fontColor} onClick={handleThoughtClick} role="button" tabIndex={0}>
                {compactText}
              </StyledSparkCardHeaderText>
            );
          }
          if (timestamp) {
            return (
              <StyledSparkCardHeaderTimestamp
                fontColor={fontColor}
                onClick={handleThoughtClick}
                role="button"
                tabIndex={0}
              >
                {timestamp}
              </StyledSparkCardHeaderTimestamp>
            );
          }
          return null;
        })()}
      </StyledSparkCardHeaderLeft>

      <StyledSparkCardHeaderReactionButton
        ref={reactionButtonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          openReactionMenu();
        }}
        aria-label="Add Reaction"
        isVisible={isAnimating}
      >
        <AddReaction style={{ color: fontColor }} />
      </StyledSparkCardHeaderReactionButton>

      {isReactionMenuOpen && (
        <ReactionMenu
          isOpen={isReactionMenuOpen}
          anchorPoint={reactionMenuCoords}
          onClose={closeReactionMenu}
          onSend={() => {}}
          onEmoji={() => {}}
          onOpenReactionsModal={() => {}}
        />
      )}
    </StyledSparkCardHeader>
  );
};

export default SparkCardHeader;
